import type { AgentEditChromeData } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import {
	getRovoTelepointerPresentation,
	resolveTelepointerPresentation,
} from '../../ui/components/TelepointerAvatar/telepointerPresentation';
import type { AgentInfo } from '../decoration/reducer';

type AgentEditChromePresentation = Pick<
	AgentEditChromeData,
	| 'agentId'
	| 'agentIdentityAccountId'
	| 'agentName'
	| 'agentNamedId'
	| 'highlightBackgroundColor'
	| 'highlightBorderColor'
	| 'highlightTextColor'
	| 'telepointerAgentKind'
	| 'telepointerLabel'
	| 'telepointerLineColor'
>;

type AgentEditChromePresentationOptions = {
	fallbackToRovo?: boolean;
	/**
	 * Use Rovo's presentation even when an identity is supplied. Rovo's provisioned account id would
	 * otherwise hash to an arbitrary agent colour.
	 */
	preferRovo?: boolean;
};

const resolvePresentation = (
	agentInfo: AgentInfo | undefined,
	{ fallbackToRovo, preferRovo }: AgentEditChromePresentationOptions,
) => {
	if (preferRovo) {
		return getRovoTelepointerPresentation();
	}
	if (agentInfo) {
		return resolveTelepointerPresentation(agentInfo);
	}
	if (fallbackToRovo) {
		return getRovoTelepointerPresentation();
	}
	return undefined;
};

/**
 * Resolves optional agent presentation at the producer boundary.
 *
 * Shares `resolveTelepointerPresentation` / `TelepointerAvatar` with the frontend
 * in-editor-direct-streaming path, behind the same `platform_editor_agent_telepointer_redesign`
 * experiment, so the observed telepointer renders the identical avatar and colour
 * treatment.
 */
export const getAgentEditChromePresentation = (
	agentInfo?: AgentInfo,
	options: AgentEditChromePresentationOptions = {},
): AgentEditChromePresentation => {
	if (!isExperimentEnabled('platform_editor_agent_mentions')) {
		return {};
	}

	if (isExperimentEnabled('platform_editor_agent_telepointer_redesign')) {
		const presentation = resolvePresentation(agentInfo, options);
		if (presentation) {
			return {
				agentId: agentInfo?.agentId,
				agentIdentityAccountId: agentInfo?.agentIdentityAccountId ?? undefined,
				agentName: agentInfo?.agentName,
				agentNamedId: agentInfo?.agentNamedId ?? undefined,
				highlightBackgroundColor: presentation.highlightBackgroundColor,
				highlightBorderColor: presentation.highlightBorderColor,
				highlightTextColor: presentation.highlightTextColor,
				telepointerAgentKind: presentation.telepointerAgentKind,
				telepointerLineColor: presentation.telepointerLineColor,
			};
		}
	}

	return agentInfo?.agentName ? { telepointerLabel: agentInfo.agentName } : {};
};

const isRovoAttribution = (agentType: string | undefined, hasIdentity: boolean): boolean =>
	agentType === 'rovo_chat' || (agentType === 'convo-ai' && !hasIdentity);

/**
 * Resolves a presentation for chrome data whose producer could not supply one.
 *
 * Producers such as collab-edit observe an agent edit through step attribution alone: they know who
 * made the change but not how it should look, and cannot resolve colours themselves. This fills in
 * that presentation so observers render the same redesigned telepointer as the requesting editor.
 * Data that already carries a resolved presentation is returned untouched.
 */
export const withResolvedAgentPresentation = (data: AgentEditChromeData): AgentEditChromeData => {
	if (data.telepointerAgentKind) {
		return data;
	}

	const agentInfo: AgentInfo = {
		agentId: data.agentId,
		agentIdentityAccountId: data.agentIdentityAccountId,
		agentName: data.agentName,
		agentNamedId: data.agentNamedId,
		isThirdParty: data.isThirdParty,
	};
	const hasIdentity = Boolean(
		agentInfo.agentId || agentInfo.agentIdentityAccountId || agentInfo.agentNamedId,
	);
	const isRovo = isRovoAttribution(data.agentType, hasIdentity);

	if (!hasIdentity && !isRovo) {
		return data;
	}

	return {
		...data,
		...getAgentEditChromePresentation(agentInfo, {
			preferRovo: isRovo,
		}),
	};
};
